import React, { useState, useEffect, useMemo } from 'react';
import { Package, DollarSign, Warehouse, Layers, Search, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const WAREHOUSES = [
  { id: 'all', name: 'Total Global', color: 'white' },
  { id: 'china', name: 'China', color: 'red' },
  { id: 'contenedor', name: 'Contenedor', color: 'orange' },
  { id: 'stock', name: 'Stock Principal', color: 'emerald' },
  { id: 'sin_stock', name: 'Sin Stock', color: 'gray' },
  { id: 'archivados', name: 'Archivados', color: 'zinc' },
];

const COLOR_MAP = {
  white: { bg: 'bg-white/[0.06]', border: 'border-white/20', text: 'text-white', active: 'bg-white text-black border-white' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', active: 'bg-red-500 text-white border-red-500' },
  orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', active: 'bg-orange-500 text-white border-orange-500' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', active: 'bg-emerald-500 text-white border-emerald-500' },
  gray: { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400', active: 'bg-gray-500 text-white border-gray-500' },
  zinc: { bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', text: 'text-zinc-400', active: 'bg-zinc-500 text-white border-zinc-500' },
};

const fmtCur = (n) => `$${Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });

const InventoryStats = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeWarehouse, setActiveWarehouse] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);
  const [sortBy, setSortBy] = useState('value');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/inventory/by-category');
      setData(res.data);
    } catch (err) {
      toast.error('Error cargando estadísticas de inventario');
    } finally {
      setLoading(false);
    }
  };

  // KPI values according to selected warehouse
  const kpis = useMemo(() => {
    const totals = data?.totals || {};
    if (activeWarehouse === 'all') {
      return {
        units: totals.total_units || 0,
        value: totals.total_value || 0,
        products: totals.total_products || 0,
        categories: totals.total_categories || 0,
      };
    }
    return {
      units: totals[activeWarehouse]?.units || 0,
      value: totals[activeWarehouse]?.value || 0,
      products: totals.total_products || 0,
      categories: totals.total_categories || 0,
    };
  }, [data, activeWarehouse]);

  const totals = data?.totals || {};

  // Filtered + sorted categories
  const visibleCategories = useMemo(() => {
    const cats = data?.categories || [];
    const filtered = search
      ? cats.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.slug.includes(search.toLowerCase()))
      : cats;
    const getUnits = (c) => (activeWarehouse === 'all' ? c.total_units : c[activeWarehouse]?.units || 0);
    const getValue = (c) => (activeWarehouse === 'all' ? c.total_value : c[activeWarehouse]?.value || 0);
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'units') return getUnits(b) - getUnits(a);
      return getValue(b) - getValue(a);
    });
    return sorted.map((c) => ({
      ...c,
      _units: getUnits(c),
      _value: getValue(c),
    }));
  }, [data, search, activeWarehouse, sortBy]);

  if (loading) {
    return (
      <div className={embedded ? 'py-8' : 'py-16'}>
        <p className="text-center text-white/40 text-sm uppercase tracking-[0.2em]">Cargando estadísticas...</p>
      </div>
    );
  }

  return (
    <div data-testid="inventory-stats" className={embedded ? '' : 'space-y-6'}>
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-emerald-400" />
              Estadísticas de Inventario
            </h2>
            <p className="text-xs text-white/40 mt-1">Capital invertido por bodega y categoría · Click para ver detalle de productos</p>
          </div>
          <button
            onClick={fetchData}
            data-testid="inventory-stats-refresh"
            className="text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2"
          >
            Refrescar
          </button>
        </div>
      )}

      {/* Warehouse selector */}
      <div className="flex flex-wrap gap-2" data-testid="inventory-stats-warehouse-tabs">
        {WAREHOUSES.map((w) => {
          const isActive = activeWarehouse === w.id;
          const colors = COLOR_MAP[w.color];
          return (
            <button
              key={w.id}
              onClick={() => setActiveWarehouse(w.id)}
              data-testid={`wh-tab-${w.id}`}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold border transition-all ${
                isActive ? colors.active : `${colors.bg} ${colors.border} ${colors.text} hover:border-white/40`
              }`}
            >
              {w.name}
              {isActive && activeWarehouse !== 'all' && (
                <span className="ml-2 opacity-80 normal-case tracking-normal">
                  · {fmtNum(totals[w.id]?.units || 0)}u
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Capital Invertido"
          value={fmtCur(kpis.value)}
          subtitle={activeWarehouse === 'all' ? 'Todas las bodegas' : WAREHOUSES.find((x) => x.id === activeWarehouse)?.name}
          accent="emerald"
          testId="kpi-invertido"
        />
        <KpiCard
          icon={<Package className="w-4 h-4" />}
          label="Total Unidades"
          value={fmtNum(kpis.units)}
          subtitle="piezas físicas"
          accent="blue"
          testId="kpi-unidades"
        />
        <KpiCard
          icon={<Layers className="w-4 h-4" />}
          label="Productos Distintos"
          value={fmtNum(kpis.products)}
          subtitle="SKUs en sistema"
          accent="white"
          testId="kpi-productos"
        />
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Categorías"
          value={fmtNum(kpis.categories)}
          subtitle="agrupaciones"
          accent="amber"
          testId="kpi-categorias"
        />
      </div>

      {/* Search + sort */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar categoría..."
            data-testid="inventory-stats-search"
            className="w-full bg-[#121212] border border-white/10 focus:border-white/30 pl-10 pr-3 py-2.5 text-sm outline-none"
          />
        </div>
        <div className="flex gap-1 bg-[#121212] border border-white/10 p-1">
          {[
            { id: 'value', label: 'Valor' },
            { id: 'units', label: 'Unidades' },
            { id: 'name', label: 'Nombre' },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setSortBy(s.id)}
              data-testid={`sort-${s.id}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors ${
                sortBy === s.id ? 'bg-white text-black' : 'text-white/50 hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Categories list */}
      <div className="space-y-2">
        {visibleCategories.length === 0 ? (
          <div className="text-center py-10 text-white/40 text-sm">No hay categorías que coincidan</div>
        ) : (
          visibleCategories.map((cat) => {
            const isOpen = expandedCat === cat.slug;
            const hasContent = cat._units > 0;
            return (
              <div
                key={cat.slug}
                data-testid={`cat-row-${cat.slug}`}
                className={`bg-[#0A0A0A] border ${isOpen ? 'border-white/30' : 'border-white/10'} transition-colors`}
              >
                <button
                  onClick={() => setExpandedCat(isOpen ? null : cat.slug)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold truncate">{cat.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-white/40 px-1.5 py-0.5 bg-white/5 border border-white/10">
                        {cat.product_count} prod
                      </span>
                    </div>
                    <div className="flex items-baseline gap-4 text-xs">
                      <span className="text-white/50">
                        <span className="text-white font-mono font-semibold tabular-nums">{fmtNum(cat._units)}</span> unidades
                      </span>
                      <span className="text-white/50">
                        <span className="text-emerald-400 font-mono font-semibold tabular-nums">{fmtCur(cat._value)}</span> invertido
                      </span>
                    </div>
                  </div>
                  {hasContent ? (isOpen ? <ChevronUp className="w-4 h-4 text-white/40 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0" />) : (
                    <span className="text-[10px] uppercase tracking-wider text-white/30">vacío</span>
                  )}
                </button>

                {isOpen && (
                  <div data-testid={`cat-detail-${cat.slug}`} className="border-t border-white/10 bg-[#070707]">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-white/40 uppercase tracking-wider text-[10px] border-b border-white/5">
                          <th className="text-left px-4 py-2.5 font-normal">Producto</th>
                          <th className="text-right px-3 py-2.5 font-normal">Costo</th>
                          <th className="text-right px-3 py-2.5 font-normal">
                            {activeWarehouse === 'all' ? 'Unidades' : `Stock ${WAREHOUSES.find(w => w.id === activeWarehouse)?.name}`}
                          </th>
                          <th className="text-right px-4 py-2.5 font-normal">Invertido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...cat.products]
                          .map((p) => {
                            const units = activeWarehouse === 'all' ? p.total_units : p.inventory?.[activeWarehouse] || 0;
                            const value = (p.cost_price || 0) * units;
                            return { ...p, _units: units, _value: value };
                          })
                          .filter((p) => p._units > 0 || activeWarehouse === 'all')
                          .sort((a, b) => b._value - a._value)
                          .map((p) => (
                            <tr
                              key={p.id}
                              data-testid={`prod-row-${p.id}`}
                              className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
                            >
                              <td className="px-4 py-2.5">
                                <p className="text-white truncate" style={{ maxWidth: '320px' }}>{p.name}</p>
                                <p className="text-[10px] text-white/40 mt-0.5">
                                  {p.brand} · {p.sku}
                                </p>
                              </td>
                              <td className="text-right px-3 py-2.5 text-white/60 font-mono tabular-nums">{fmtCur(p.cost_price)}</td>
                              <td className="text-right px-3 py-2.5 font-mono tabular-nums">
                                <span className={p._units === 0 ? 'text-white/30' : 'text-white'}>{fmtNum(p._units)}</span>
                              </td>
                              <td className="text-right px-4 py-2.5 font-mono tabular-nums text-emerald-400 font-semibold">
                                {fmtCur(p._value)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-white/[0.03] text-[11px]">
                          <td className="px-4 py-2.5 uppercase tracking-wider text-white/60">Subtotal categoría</td>
                          <td className="px-3 py-2.5"></td>
                          <td className="text-right px-3 py-2.5 font-mono tabular-nums text-white">{fmtNum(cat._units)}</td>
                          <td className="text-right px-4 py-2.5 font-mono tabular-nums text-emerald-400 font-semibold">{fmtCur(cat._value)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Grand total footer */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-500/0 border border-emerald-500/30 p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="inventory-stats-footer">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/80">
            {activeWarehouse === 'all' ? 'Capital Total Invertido' : `Capital Invertido · ${WAREHOUSES.find(w => w.id === activeWarehouse)?.name}`}
          </p>
          <p className="text-2xl md:text-3xl font-mono font-bold tabular-nums text-emerald-300 mt-1">
            {fmtCur(kpis.value)}
          </p>
        </div>
        <div className="text-right md:text-right">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Total piezas</p>
          <p className="text-lg md:text-xl font-mono tabular-nums text-white mt-1">{fmtNum(kpis.units)}</p>
        </div>
      </div>
    </div>
  );
};

const ACCENT_MAP = {
  emerald: 'border-emerald-500/30 text-emerald-400',
  blue: 'border-blue-500/30 text-blue-400',
  amber: 'border-amber-500/30 text-amber-400',
  white: 'border-white/15 text-white',
};

const KpiCard = ({ icon, label, value, subtitle, accent = 'white', testId }) => (
  <div data-testid={testId} className={`bg-[#0A0A0A] border ${ACCENT_MAP[accent]} p-4`}>
    <div className="flex items-center gap-2 mb-2 opacity-80">
      {icon}
      <span className="text-[10px] uppercase tracking-[0.2em]">{label}</span>
    </div>
    <p className="text-lg md:text-xl font-mono font-bold tabular-nums text-white">{value}</p>
    <p className="text-[10px] text-white/40 mt-1 truncate">{subtitle}</p>
  </div>
);

export default InventoryStats;
